// src/pages/PricingUpdate.js
import React, { useState } from "react";
import { db } from "../firebase/firebase";
import { updateDoc, doc, serverTimestamp } from "firebase/firestore";

import { useClients } from "../hooks/useClients";
import { usePricingTrips } from "../hooks/usePricingTrips";
import { collection, getDocs } from "firebase/firestore";
import { exportTripsToExcel } from "../utils/exportTripsToExcel";


export default function PricingUpdate() {
  const [batchDate, setBatchDate] = useState("");
  const [fromDate, setFromDate] = useState("");
const [toDate, setToDate] = useState("");


  const { clientId, clientList, handleClientSelect } = useClients();

  const {
    rows,
    setRows,
    loading,
    filter,
    setFilter,
    hasMore,
    loadTrips,
  } = usePricingTrips({ clientId, batchDate, pageSize: 100 });

  // ✅ Update price values (no auto-lock)
  async function updatePrice(r, field, value) {
    const v = value === "" ? "" : Number(value);

    r[field] = v;

    const clientPrice =
      r.clientFinalPrice === "" || r.clientFinalPrice == null
        ? 0
        : Number(r.clientFinalPrice || 0);
    const driverPrice =
      r.driverFinalPrice === "" || r.driverFinalPrice == null
        ? 0
        : Number(r.driverFinalPrice || 0);

    const clientParking =
      r.clientParking === "" || r.clientParking == null
        ? 0
        : Number(r.clientParking || 0);
    const driverParking =
      r.driverParking === "" || r.driverParking == null
        ? 0
        : Number(r.driverParking || 0);

    r.totalClient = clientPrice + clientParking;
    r.totalDriver = driverPrice + driverParking;

    setRows([...rows]);

    try {
      const batchKey = r.realBatchKey || `${clientId}_${batchDate}`;

      await updateDoc(doc(db, `tripImports/${batchKey}/trips/${r.id}`), {
        [field]: v === "" ? null : Number(v),
        totalClient: r.totalClient,
        totalDriver: r.totalDriver,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      alert("Failed to save pricing update.");
    }
  }

  async function markPricingDone(r) {
    try {
      const batchKey = r.realBatchKey || `${clientId}_${batchDate}`;

      await updateDoc(doc(db, `tripImports/${batchKey}/trips/${r.id}`), {
        pricingDone: true,
        updatedAt: serverTimestamp(),
      });

      r.pricingDone = true;
      setRows([...rows]);
    } catch (e) {
      console.error(e);
      alert("Failed to mark pricing as done.");
    }
  }

  async function unlockPricing(r) {
    try {
      const batchKey = r.realBatchKey || `${clientId}_${batchDate}`;

      await updateDoc(doc(db, `tripImports/${batchKey}/trips/${r.id}`), {
        pricingDone: false,
        updatedAt: serverTimestamp(),
      });

      r.pricingDone = false;
      setRows([...rows]);
    } catch (e) {
      console.error(e);
      alert("Failed to unlock row.");
    }
  }

    // ------------------------------------------------------
  // ⭐ EXPORT PRICING TO EXCEL (ALL CLIENTS or SINGLE CLIENT)
  // ------------------------------------------------------
  async function exportPricingExcel() {
    if (!fromDate || !toDate) {
      alert("Please select From + To dates.");
      return;
    }

    let allTrips = [];

    try {
      // Load all trip import folders
      const importsSnap = await getDocs(collection(db, "tripImports"));

      for (const folder of importsSnap.docs) {
        const batchKey = folder.id; // eg: koi_technologies_fzco_2025-11-13

        // ---- Extract the date (last part)
        const parts = batchKey.split("_");
        const date = parts[parts.length - 1]; // "2025-11-13"

        // ---- Extract clientId safely (all except last)
        const cId = parts.slice(0, parts.length - 1).join("_");

        // 🔹 Client filter
        if (clientId !== "all_clients" && clientId !== cId) continue;

        // 🔹 Date filter
        if (date < fromDate || date > toDate) continue;

        // 🔹 Load trips for this batch
        const tripsRef = collection(db, `tripImports/${batchKey}/trips`);
        const tripsSnap = await getDocs(tripsRef);

        tripsSnap.forEach((t) =>
          allTrips.push({
            id: t.id,
            ...t.data(),
            realBatchKey: batchKey,
            clientKey: cId,
            batchDate: date,
          })
        );
      }

      if (allTrips.length === 0) {
        alert("⚠ No trips found for selected range.");
        return;
      }

      await exportTripsToExcel(
        allTrips,
        `pricing_${clientId}_${fromDate}_${toDate}.xlsx`
      );

      alert("✅ Pricing Excel exported successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to export pricing Excel.");
    }
  }


  return (
    <div style={{ padding: 20 }}>
      <h2>💰 Pricing Update</h2>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
              {/* Export by date range */}
      <div
        style={{
          marginBottom: 15,
          display: "flex",
          gap: 10,
          alignItems: "center",
        }}
      >
        <label>From:</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />

        <label>To:</label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />

        <button
          onClick={exportPricingExcel}
          style={{
            padding: "6px 12px",
            background: "green",
            color: "#fff",
            borderRadius: 6,
            fontWeight: 600,
          }}
        >
          ⬇ Export Pricing (Excel)
        </button>
      </div>

        <label>Client:</label>
        <select value={clientId} onChange={handleClientSelect}>
          <option value="">Select client</option>
          {clientList.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
          <option value="add_new">➕ Add New Client</option>
        </select>

        <label style={{ marginLeft: 12 }}>Date:</label>
        <input
          type="date"
          value={batchDate}
          onChange={(e) => setBatchDate(e.target.value)}
        />

        <label style={{ marginLeft: 12 }}>Filter:</label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="pending">⏳ Pending</option>
          <option value="done">✅ Done</option>
          <option value="all">📋 All</option>
        </select>

        <button
          onClick={() => loadTrips(true)}
          style={{
            padding: "6px 12px",
            background: "#0066cc",
            color: "#fff",
            borderRadius: 6,
            fontWeight: 600,
          }}
        >
          Load
        </button>
      </div>

      {loading && <p>⏳ Loading trips…</p>}

      {rows.length > 0 && (
        <>
          <p style={{ marginBottom: 10 }}>
            Showing <b>{rows.length}</b> trips{" "}
            {filter !== "all" && (
              <span>
                ({filter === "pending" ? "Pending only" : "Completed only"})
              </span>
            )}
          </p>

          <table
            border="1"
            cellPadding="6"
            style={{ background: "#fff", width: "100%", borderCollapse: "collapse" }}
          >
            <thead style={{ background: "#f8f8f8" }}>
              <tr>
                <th>#</th>
                <th>Client Trip ID</th>
                <th>CLL Trip ID</th>
                <th>Passenger</th>
                <th>Phone</th>
                <th>No. of Pax</th>
                <th>Flight No</th>
                <th>Pickup</th>
                <th>Drop</th>
                <th>Car Type</th>
                <th>Driver</th>
                <th>Actual Parking</th>
                <th>Client Price</th>
                <th>Driver Price</th>
                <th>Client Parking</th>
                <th>Driver Parking</th>
                <th>Total Client</th>
                <th>Total Driver</th>
                <th>KOI Price Check</th>
                <th>Comments</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={r.id}
                  style={{
                    background: r.pricingDone ? "#e8fbe8" : "#fff9db",
                  }}
                >
                  <td>{i + 1}</td>
                  <td>{r.reservationId}</td>
                  <td>{r.cllTripId}</td>
                  <td>{r.passengerName}</td>
                  <td>{r.passengerPhone}</td>
                  <td>{r.noOfPassengers}</td>
                  <td>{r.flightNumber}</td>
                  <td>{r.pickup}</td>
                  <td>{r.drop}</td>
                  <td>{r.carType}</td>
                  <td>{r.driver}</td>

                  <td>
                    <input
                      type="number"
                      defaultValue={r.actualParking || ""}
                      onBlur={(e) =>
                        updatePrice(r, "actualParking", e.target.value)
                      }
                      style={{ width: 90 }}
                      disabled={r.pricingDone}
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      defaultValue={r.clientFinalPrice ?? ""}
                      onBlur={(e) =>
                        updatePrice(r, "clientFinalPrice", e.target.value)
                      }
                      style={{ width: 90 }}
                      disabled={r.pricingDone}
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      defaultValue={r.driverFinalPrice ?? ""}
                      onBlur={(e) =>
                        updatePrice(r, "driverFinalPrice", e.target.value)
                      }
                      style={{ width: 90 }}
                      disabled={r.pricingDone}
                    />
                  </td>

                  <td>{r.clientParking}</td>
                  <td>{r.driverParking}</td>
                  <td>{r.totalClient}</td>
                  <td>{r.totalDriver}</td>
                  {/* ⭐ KOI VALIDATION CELL */}
<td
  style={{
    background:
      r.priceStatus === "❌ WRONG PRICE"
        ? "#ffb3b3"
        : r.priceStatus?.startsWith("⚠")
        ? "#fff3cd"
        : "#d4edda",
    fontWeight: 600,
    minWidth: "150px",
  }}
>
  {r.koiExpectedPrice > 0 ? (
    <>
      Expected: {r.koiExpectedPrice} <br />
      Imported: {r.clientFinalPrice} <br />
      <b>{r.priceStatus}</b>
    </>
  ) : (
    r.priceStatus
  )}
</td>

                  <td>{r.comment}</td>

                  <td>
                    {r.pricingDone ? (
                      <button
                        style={{
                          padding: "2px 8px",
                          background: "#ffcc00",
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                        onClick={() => unlockPricing(r)}
                      >
                        ✏️ Unlock
                      </button>
                    ) : (
                      <button
                        style={{
                          padding: "2px 8px",
                          background: "#00b894",
                          color: "#fff",
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                        onClick={() => markPricingDone(r)}
                      >
                        ✔ Mark Done
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {hasMore && (
            <button
              onClick={() => loadTrips(false)}
              style={{
                marginTop: 12,
                padding: "8px 14px",
                background: "#008CBA",
                color: "#fff",
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Load More Trips ⬇️
            </button>
          )}
        </>
      )}
    </div>
  );
}
